/* Access wrapper for localStorage values */

import {
  IConvosInputCreate,
  IUserInputOneSignalUserDeviceAssociation,
} from '@/api/types/request.types'
import { ICreateItemStorage } from '@/types/data-objects/items'

export default new class LocalStore {
  /* Keys */
  private key_DashboardViewType: string = 'dashboardViewType'
  private key_DashboardHidePinboard: string = 'dashboardHidePinboard'
  private key_DashboardProjectsFirst: string = 'DashboardProjectsFirst'
  private key_WorkflowsShowMailboxesFirst: string =
    'workflowsShowMailboxesFirst'
  private key_DefaultConvoToHtmlDesc: string = 'defaultConvoToHtmlDesc'
  private key_ProjectScheduleViewType: string = 'projectScheduleViewType'

  private key_ConvoCreateDataPrefix: string = 'convoCreateData_'
  private key_TaskCreateDataPrefix: string = 'taskCreateData_'
  private key_HomeQueryParamsPrefix: string = 'entityHomeSavedQuery_'
  private key_CommentOrReplyTextPrefix: string = 'commentOrReply_text_'
  private key_DefaultToReplyModePrefix: string = 'defaultToReplyMode_'
  private key_showInfoOnEntityLandingPrefix: string = 'showInfoOnEntityLanding_'
  private key_ProjectDefaultedToKanbanPrefix: string =
    'projectDefaultedToKanban_'
  private key_MailboxDefaultedToKanbanPrefix: string =
    'mailboxDefaultedToKanban_'
  private key_KanbanCollapsedColumnsPrefix: string = 'kanbanCollapsedColumns_'

  private key_AllTasksQueryParams: string = 'allTasksQuery_'
  private key_AllConvosQueryParams: string = 'allConvosQuery_'

  private key_itemDetailBackBtnToRoot: string = 'itemDetailBackBtnToRoot'
  private key_lastItemListVisited: string = 'lastItemListVisited'

  private key_mailboxForwardingAddressCandidate: string =
    'mailboxForwardingAddressCandidate'

  /* This keys is set in index.html before the Vue app is rendered
     so that we can send this data back thru the normal Vue API commands.
     I cannot call the API call on the index.html page because the cookies
     aren't set yet. Also, these vars below are hardcoded into the index.html
     page since it cannot access this class obviously. */
  private key_oneSignalUserDeviceAssociationInfo: string =
    'oneSignalUserDeviceAssociationInfo'

  /* OneSignal user device association info */
  public get oneSignalUserDeviceAssociationInfo(): IUserInputOneSignalUserDeviceAssociation {
    return JSON.parse(
      localStorage.getItem(this.key_oneSignalUserDeviceAssociationInfo),
    )
  }

  public clearOneSignalUserDeviceAssociationInfo() {
    localStorage.removeItem(this.key_oneSignalUserDeviceAssociationInfo)
  }

  /* Mailbox forwarding address candidate */
  public get mailboxForwardingAddressCandidate(): string {
    return localStorage.getItem(this.key_mailboxForwardingAddressCandidate)
  }

  public set mailboxForwardingAddressCandidate(value: string) {
    localStorage.setItem(this.key_mailboxForwardingAddressCandidate, value)
  }

  public clearMailboxForwardingAddressCandidate() {
    localStorage.removeItem(this.key_mailboxForwardingAddressCandidate)
  }

  /* Dashboard view type */
  public get dashboardViewType(): string {
    return localStorage.getItem(this.key_DashboardViewType)
  }

  public set dashboardViewType(value: string) {
    localStorage.setItem(this.key_DashboardViewType, value)
  }

  /* Project schedule view type */
  public get projectScheduleViewType(): string {
    return localStorage.getItem(this.key_ProjectScheduleViewType)
  }

  public set projectScheduleViewType(value: string) {
    localStorage.setItem(this.key_ProjectScheduleViewType, value)
  }

  /* Dashboard show pinboard */
  public get dashboardHidePinboard(): boolean {
    return localStorage.getItem(this.key_DashboardHidePinboard) === 'true'
  }

  public set dashboardHidePinboard(value: boolean) {
    localStorage.setItem(this.key_DashboardHidePinboard, value.toString())
  }

  /* Dashboard projects first */
  public get dashboardProjectsFirst(): boolean {
    return localStorage.getItem(this.key_DashboardProjectsFirst) === 'true'
  }

  public set dashboardProjectsFirst(value: boolean) {
    localStorage.setItem(this.key_DashboardProjectsFirst, value.toString())
  }

  /* Workflows default entity */
  public get workflowsShowMailboxesFirst(): boolean {
    return localStorage.getItem(this.key_WorkflowsShowMailboxesFirst) === 'true'
  }

  public set workflowsShowMailboxesFirst(value: boolean) {
    localStorage.setItem(this.key_WorkflowsShowMailboxesFirst, value.toString())
  }

  /* Convo detail: Default to HTML description? */
  public get defaultToHtmlDescriptionOnConvoDetail(): boolean {
    return localStorage.getItem(this.key_DefaultConvoToHtmlDesc) === 'true'
  }

  public set defaultToHtmlDescriptionOnConvoDetail(value: boolean) {
    localStorage.setItem(this.key_DefaultConvoToHtmlDesc, value.toString())
  }

  /* Convo create data */
  public getConvoCreateData(mailboxID: number): ICreateItemStorage {
    return JSON.parse(
      localStorage.getItem(
        this.key_ConvoCreateDataPrefix + mailboxID.toString(),
      ),
    )
  }

  public setConvoCreateData(mailboxID: number, value: ICreateItemStorage) {
    localStorage.setItem(
      this.key_ConvoCreateDataPrefix + mailboxID.toString(),
      JSON.stringify(value),
    )
  }

  public clearConvoCreateData(mailboxID: number) {
    localStorage.removeItem(
      this.key_ConvoCreateDataPrefix + mailboxID.toString(),
    )
  }

  /* Task create data */
  public getTaskCreateData(projectID: number): ICreateItemStorage {
    return JSON.parse(
      localStorage.getItem(
        this.key_TaskCreateDataPrefix + projectID.toString(),
      ),
    )
  }

  public setTaskCreateData(projectID: number, value: ICreateItemStorage) {
    localStorage.setItem(
      this.key_TaskCreateDataPrefix + projectID.toString(),
      JSON.stringify(value),
    )
  }

  public clearTaskCreateData(projectID: number) {
    localStorage.removeItem(
      this.key_TaskCreateDataPrefix + projectID.toString(),
    )
  }

  /* Home saved query */
  public getHomeSavedQuery(entityID: number): string {
    return localStorage.getItem(
      this.key_HomeQueryParamsPrefix + entityID.toString(),
    )
  }

  public setHomeSavedQuery(entityID: number, value: any) {
    localStorage.setItem(
      this.key_HomeQueryParamsPrefix + entityID.toString(),
      JSON.stringify(value),
    )
  }

  public clearHomeSavedQuery(entityID: number) {
    localStorage.removeItem(
      this.key_HomeQueryParamsPrefix + entityID.toString(),
    )
  }

  /* All convos saved query */
  public getAllConvosSavedQuery(accountID: number): string {
    return localStorage.getItem(
      this.key_AllConvosQueryParams + accountID.toString(),
    )
  }

  public setAllConvosSavedQuery(accountID: number, value: any) {
    localStorage.setItem(
      this.key_AllConvosQueryParams + accountID.toString(),
      JSON.stringify(value),
    )
  }

  public clearAllConvosSavedQuery(accountID: number) {
    localStorage.removeItem(
      this.key_AllConvosQueryParams + accountID.toString(),
    )
  }

  /* All tasks saved query */
  public getAllTasksSavedQuery(accountID: number): string {
    return localStorage.getItem(
      this.key_AllTasksQueryParams + accountID.toString(),
    )
  }

  public setAllTasksSavedQuery(accountID: number, value: any) {
    localStorage.setItem(
      this.key_AllTasksQueryParams + accountID.toString(),
      JSON.stringify(value),
    )
  }

  public clearAllTasksSavedQuery(accountID: number) {
    localStorage.removeItem(this.key_AllTasksQueryParams + accountID.toString())
  }

  /* Comment or reply text */
  public getCommentOrReplyText(itemID: number): string {
    return localStorage.getItem(
      this.key_CommentOrReplyTextPrefix + itemID.toString(),
    )
  }

  public setCommentOrReplyText(itemID: number, value: any) {
    localStorage.setItem(
      this.key_CommentOrReplyTextPrefix + itemID.toString(),
      value,
    )
  }

  public clearCommentOrReplyText(itemID: number) {
    localStorage.removeItem(
      this.key_CommentOrReplyTextPrefix + itemID.toString(),
    )
  }

  /* Comment or reply mode */
  public isDefaultToReplyMode(itemID: number): boolean {
    return (
      localStorage.getItem(
        this.key_DefaultToReplyModePrefix + itemID.toString(),
      ) === 'true'
    )
  }

  public setDefaultToReplyMode(itemID: number, value: any) {
    localStorage.setItem(
      this.key_DefaultToReplyModePrefix + itemID.toString(),
      value,
    )
  }

  public clearDefaultToReplyMode(itemID: number) {
    localStorage.removeItem(
      this.key_DefaultToReplyModePrefix + itemID.toString(),
    )
  }

  /* Show info on entity landing */
  public showInfoOnEntityLanding(entityID: number): boolean {
    return (
      localStorage.getItem(
        this.key_showInfoOnEntityLandingPrefix + entityID.toString(),
      ) === 'true'
    )
  }

  public setShowInfoOnEntityLanding(entityID: number) {
    localStorage.setItem(
      this.key_showInfoOnEntityLandingPrefix + entityID.toString(),
      'true',
    )
  }

  public clearShowInfoOnEntityLanding(entityID: number) {
    localStorage.removeItem(
      this.key_showInfoOnEntityLandingPrefix + entityID.toString(),
    )
  }

  /* Default project to Kanban */
  public isProjectDefaultedToKanban(projectID: number | string): boolean {
    return (
      localStorage.getItem(
        this.key_ProjectDefaultedToKanbanPrefix + projectID.toString(),
      ) === '1'
    )
  }

  public setProjectDefaultedToKanban(projectID: number) {
    localStorage.setItem(
      this.key_ProjectDefaultedToKanbanPrefix + projectID.toString(),
      '1',
    )
  }

  public clearProjectDefaultedToKanban(projectID: number) {
    localStorage.removeItem(
      this.key_ProjectDefaultedToKanbanPrefix + projectID.toString(),
    )
  }

  /* Default mailbox to Kanban */
  public isMailboxDefaultedToKanban(mailboxID: number | string): boolean {
    return (
      localStorage.getItem(
        this.key_MailboxDefaultedToKanbanPrefix + mailboxID.toString(),
      ) === '1'
    )
  }

  public setMailboxDefaultedToKanban(mailboxID: number) {
    localStorage.setItem(
      this.key_MailboxDefaultedToKanbanPrefix + mailboxID.toString(),
      '1',
    )
  }

  public clearMailboxDefaultedToKanban(mailboxID: number) {
    localStorage.removeItem(
      this.key_MailboxDefaultedToKanbanPrefix + mailboxID.toString(),
    )
  }

  /* Kanban collapsed columns (per entity, list of collapsed status IDs) */
  public getKanbanCollapsedColumns(entityID: number): number[] {
    try {
      const value = localStorage.getItem(
        this.key_KanbanCollapsedColumnsPrefix + entityID.toString(),
      )
      return value ? JSON.parse(value) : []
    } catch {
      return []
    }
  }

  public setKanbanCollapsedColumns(entityID: number, statusIDs: number[]) {
    localStorage.setItem(
      this.key_KanbanCollapsedColumnsPrefix + entityID.toString(),
      JSON.stringify(statusIDs),
    )
  }

  /* Item detail back button to root (All, project/mailbox home)? (Vs. Just going router.go(-1)) */
  public get itemDetailBackBtnToRoot(): boolean {
    return localStorage.getItem(this.key_itemDetailBackBtnToRoot) === 'true'
  }

  public set itemDetailBackBtnToRoot(value: boolean) {
    localStorage.setItem(this.key_itemDetailBackBtnToRoot, value.toString())
  }

  public get lastItemListRouteVisited(): any {
    return JSON.parse(localStorage.getItem(this.key_lastItemListVisited))
  }

  public set lastItemListRouteVisited(value: any) {
    localStorage.setItem(this.key_lastItemListVisited, JSON.stringify(value))
  }
}()
